import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { UnifiedStatusBadge } from './unified';
import StatusActions from './StatusActions';

const ACTIVITY_TYPE_ICONS = {
  FERTILIZATION: '🌿',
  CROP_PROTECTION: '💧',
  TILLAGE: '🚜',
  SOWING: '🌱',
  HARVEST: '🌾',
  OTHER: '📋',
};

const PRIORITY_BADGE = {
  HIGH: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  MEDIUM: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  LOW: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Decode JWT claims (no signature check — validation is server-side). */
function getRoleFromToken(token) {
  try {
    return JSON.parse(atob(token.split('.')[1])).role || 'owner';
  } catch {
    return 'owner';
  }
}

// ── Activity Confirmation Card ────────────────────────────────────────────────

function ActivityConfirmationCard({ card, token, onDeleted, onShowDetail }) {
  const { t } = useTranslation();
  const [deleted, setDeleted] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState(card.status || 'APPROVED');

  const icon = ACTIVITY_TYPE_ICONS[card.activity_type] || '📋';
  const userRole = token ? getRoleFromToken(token) : 'owner';

  const handleDelete = async () => {
    if (!token || deleting) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/v1/aif/farm/activities/${card.id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      setDeleted(true);
      onDeleted?.();
    } catch { /* ApiError */ }
    setDeleting(false);
  };

  if (deleted) {
    return (
      <div className="text-xs text-[var(--aif-text-muted)] py-1" data-testid="card-deleted">
        ✗ {t('confirm.delete')}
      </div>
    );
  }

  return (
    <div
      className="mt-2 border border-[var(--aif-color-success)]/30 bg-[var(--aif-color-success)]/5 rounded-xl p-3 text-sm"
      data-testid="activity-confirmation-card"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-base">✅</span>
        <span className="font-semibold text-[var(--aif-text-primary)]">{t('confirm.activityTitle')}</span>
        <span className="ml-auto">
          <UnifiedStatusBadge status={status} />
        </span>
      </div>

      <div className="space-y-1 text-xs text-[var(--aif-text-secondary)]">
        <div className="flex items-center gap-1.5">
          <span>{icon}</span>
          <span className="font-medium text-[var(--aif-text-primary)]">{card.activity_type}</span>
        </div>
        {card.field_name && (
          <div>
            <span className="text-[var(--aif-text-muted)]">{t('confirm.field')}:</span>{' '}
            <span>{card.field_name}</span>
          </div>
        )}
        {card.date && (
          <div>
            <span className="text-[var(--aif-text-muted)]">{t('confirm.date')}:</span>{' '}
            <span>{fmtDate(card.date)}</span>
          </div>
        )}
        {card.product_name && (
          <div>
            <span className="text-[var(--aif-text-muted)]">{t('confirm.product')}:</span>{' '}
            <span>{card.product_name}</span>
            {card.quantity && <span> {card.quantity} {card.unit}</span>}
          </div>
        )}
        {card.nutrient_n_kg_ha != null && (
          <div>
            <span className="text-[var(--aif-text-muted)]">{t('confirm.nutrientN')}:</span>{' '}
            <span className="font-medium text-green-600 dark:text-green-400">
              {t('confirm.kgNHa', { val: card.nutrient_n_kg_ha })}
            </span>
          </div>
        )}
      </div>

      <div className="mt-2.5 pt-2 border-t border-[var(--aif-border-default)] space-y-2">
        {token && (
          <StatusActions
            eventTable="aif_field_activities"
            eventId={card.id}
            currentStatus={status}
            userRole={userRole}
            token={token}
            onTransition={(newStatus) => setStatus(newStatus)}
          />
        )}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-[var(--aif-text-muted)]">{t('confirm.correctHint')}</span>
          <div className="flex items-center gap-3">
            {onShowDetail && card.field_id && (
              <button
                onClick={() => onShowDetail('fields', { fieldId: card.field_id, tab: 'activities' })}
                className="text-xs text-[var(--aif-color-primary)] hover:underline"
                data-testid="activity-view-field-button"
              >
                {t('confirm.viewField')}
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="text-xs text-red-400 hover:text-red-500 transition disabled:opacity-50"
              data-testid="activity-delete-button"
            >
              {deleting ? '…' : t('confirm.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Task Confirmation Card ────────────────────────────────────────────────────

function TaskConfirmationCard({ card, token, onDeleted, onShowDetail }) {
  const { t } = useTranslation();
  const [deleted, setDeleted] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState(card.status || 'DRAFT');

  const icon = ACTIVITY_TYPE_ICONS[card.activity_type] || '📋';
  const badgeClass = PRIORITY_BADGE[card.priority] || PRIORITY_BADGE.MEDIUM;
  const userRole = token ? getRoleFromToken(token) : 'owner';

  const handleDelete = async () => {
    if (!token || deleting) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/v1/aif/farm/tasks/${card.id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      setDeleted(true);
      onDeleted?.();
    } catch { /* ApiError */ }
    setDeleting(false);
  };

  if (deleted) {
    return (
      <div className="text-xs text-[var(--aif-text-muted)] py-1" data-testid="card-deleted">
        ✗ {t('confirm.delete')}
      </div>
    );
  }

  return (
    <div
      className="mt-2 border border-[var(--aif-color-primary)]/30 bg-[var(--aif-color-primary)]/5 rounded-xl p-3 text-sm"
      data-testid="task-confirmation-card"
      data-task-confirmation
    >
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-base">📋</span>
        <span className="font-semibold text-[var(--aif-text-primary)]">{t('confirm.taskTitle')}</span>
        <span className="ml-auto">
          <UnifiedStatusBadge status={status} />
        </span>
      </div>

      <div className="space-y-1 text-xs text-[var(--aif-text-secondary)]">
        <div className="font-medium text-[var(--aif-text-primary)]">{card.title}</div>
        {card.field_name && (
          <div>
            <span className="text-[var(--aif-text-muted)]">{t('confirm.field')}:</span>{' '}
            <span>{card.field_name}</span>
          </div>
        )}
        {card.activity_type && (
          <div className="flex items-center gap-1">
            <span>{icon}</span>
            <span>{card.activity_type}</span>
          </div>
        )}
        {card.due_date && (
          <div>
            <span className="text-[var(--aif-text-muted)]">{t('confirm.dueDate')}:</span>{' '}
            <span>{fmtDate(card.due_date)}</span>
          </div>
        )}
        {card.priority && (
          <div className="flex items-center gap-1">
            <span className="text-[var(--aif-text-muted)]">{t('confirm.priority')}:</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${badgeClass}`}>
              {card.priority}
            </span>
          </div>
        )}
        {card.weather_dependent && (
          <div className="text-[var(--aif-text-muted)]">🌤️ {t('confirm.weatherDependent')}</div>
        )}
      </div>

      <div className="mt-2.5 pt-2 border-t border-[var(--aif-border-default)] space-y-2">
        {token && (
          <StatusActions
            eventTable="aif_farm_tasks"
            eventId={card.id}
            currentStatus={status}
            userRole={userRole}
            token={token}
            onTransition={(newStatus) => setStatus(newStatus)}
          />
        )}
        <div className="flex items-center justify-between">
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-xs text-red-400 hover:text-red-500 transition disabled:opacity-50"
            data-testid="task-delete-button"
          >
            {deleting ? '…' : t('confirm.delete')}
          </button>
          {onShowDetail && (
            <button
              onClick={() => onShowDetail('weekly')}
              className="text-xs text-[var(--aif-color-primary)] hover:underline"
              data-testid="task-view-weekly-button"
            >
              {t('confirm.viewAllTasks')} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Field Disambiguation Card ─────────────────────────────────────────────────

function FieldDisambiguationCard({ card, onSendMessage }) {
  const { t } = useTranslation();
  const fields = card.fields || [];

  const handleFieldClick = (field) => {
    if (!onSendMessage) return;
    // Re-send the original message with the chosen field name appended so the
    // parser can resolve the field reference on the second attempt.
    const corrected = `${card.original_message} (Feld: ${field.name})`;
    onSendMessage(corrected);
  };

  return (
    <div
      className="mt-2 border border-yellow-400/40 bg-yellow-50/60 dark:bg-yellow-900/15 rounded-xl p-3 text-sm"
      data-testid="field-disambiguation-card"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-base">🗺️</span>
        <span className="font-semibold text-[var(--aif-text-primary)]">
          {card.message || t('confirm.whichField')}
        </span>
      </div>
      {fields.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {fields.map((f) => (
            <button
              key={f.id}
              onClick={() => handleFieldClick(f)}
              className="px-2.5 py-1 rounded-full text-xs font-medium border border-[var(--aif-color-primary)]/40 text-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/8 hover:bg-[var(--aif-color-primary)] hover:text-white transition-all active:scale-95"
              data-testid="field-chip"
              data-disambiguation-chip
            >
              {f.name}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-[var(--aif-text-muted)]">{t('confirm.noFieldFound')}</p>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function ConfirmationCard({ card, token, onDeleted, onShowDetail, onSendMessage }) {
  if (!card) return null;

  if (card.type === 'activity_confirmation') {
    return <ActivityConfirmationCard card={card} token={token} onDeleted={onDeleted} onShowDetail={onShowDetail} />;
  }
  if (card.type === 'task_confirmation') {
    return <TaskConfirmationCard card={card} token={token} onDeleted={onDeleted} onShowDetail={onShowDetail} />;
  }
  if (card.type === 'field_disambiguation') {
    return <FieldDisambiguationCard card={card} onSendMessage={onSendMessage} />;
  }
  return null;
}
